import type { MuscleSlug } from "./bodymap.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import { exercisePainSeverity } from "./painRisk.js";
import { painSides, type PainMap } from "./painSides.js";
import type { LastLoggedAnySet } from "./lastLoggedSets.js";
import type { Exercise } from "./schemas/exercise.js";
import type { PlannedExerciseEntry } from "./schemas/plan.js";
import { BODY_SIDES, type Side } from "./side.js";
import { calendarDaysBetween } from "./timeRange.js";
import { TODAY_TARGET_RIR, type TodayAnchor, type TodayHistory } from "./todayAnchor.js";
import { regapTodayRows, todaySetCap } from "./todayRegap.js";

// AP-9b, «Build today's session»: the SELECTION, and nothing else. The AP-9
// panel already found the muscles under MEV and ranked the exercises that
// close each gap (volumeGapProposal.ts). This picks one exercise per muscle
// for today, drops what the pain map vetoes, and hands back planned entries
// for buildPlanStarter plus the reasons the logger's draft card shows.
// Pure: no fetch, no clock, no React. AP-9c: each row keeps its alternatives;
// the time budget and the shuffle that read them are in todayDraftEdit.ts.

/** The rest the athlete picks, in elapsed hours (Antenore, 07/10): a muscle
 *  trained more recently stays out of today's draft. Shown in the draft's
 *  scope line, so it is never a hidden rule. */
export const TODAY_REST_HOURS_CHOICES = [48, 72] as const;
export type TodayRestHours = (typeof TODAY_REST_HOURS_CHOICES)[number];
export const DEFAULT_REST_HOURS: TodayRestHours = 48;

/** A stored choice, or null when it is not one of the two. */
export const parseTodayRestHours = (raw: string): TodayRestHours | null => {
  const v = Number(raw);
  return TODAY_REST_HOURS_CHOICES.find((c) => c === v) ?? null;
};

const HOUR_MS = 3_600_000;

/** Candidates per muscle the draft reads (AP-9c): room for the shuffle. The
 *  Progress list keeps its own 3 (GAP_LIST_MAX_PER_MUSCLE). */
export const TODAY_CANDIDATES_PER_MUSCLE = 8;

/** What a gap counts toward. "mav": a muscle with no MEV gap, still under
 *  MAV; its row only fills time the MEV rows leave (Antenore, 26/09). */
export type TodayTarget = "mev" | "mav";

/** One muscle under MEV, as the AP-9 panel reads it, or under MAV. */
export type TodayGap = {
  muscle: CatalogMuscleSlug;
  /** Sets a week below the target, on the 4-full-week average. */
  gap: number;
  /** Absent: "mev". */
  target?: TodayTarget;
  /** Last session that gave this muscle a hard set; null = never. */
  lastTrainedMs: number | null;
  /** D3: the band value of `target` (MEV, or MAV). With `weekSets`, today's
   *  gap is targetSets × intensity − weekSets (regapTodayRows). Absent: the
   *  row keeps `gap` as given. */
  targetSets?: number;
  /** Hard sets done this calendar week, synergist credit included. */
  weekSets?: number;
  /** Ranked best first, as proposeForVolumeGaps returns them. `weight` is the
   *  recruitment the ranking read; absent reads as a tie. */
  candidates: ReadonlyArray<{ slug: string; setsToClose: number; weight?: number }>;
};

/** One exercise a draft row can hold. */
export type TodayAlternative = {
  exerciseId: string;
  setsToClose: number;
  /** The reps and the set the load chain reads (todayAnchor.ts); null =
   *  never done and nothing similar. */
  anchor?: TodayAnchor | null;
  /** LEGACY: a draft saved before the P0 fix (01/10) has no `anchor` and no
   *  `targetSets`, so it reads this last set and keeps its 4-week gap. The
   *  branches that read it (here, todayPlannedEntries, todayChainSets,
   *  regapTodayRows) can go once a release after the fix has shipped: a
   *  draft lives only until Start or discard. */
  last?: LastLoggedAnySet | null;
};

/** The exercise has history of its own: its reps, and kg when it has a load. */
export const altHasHistory = (a: TodayAlternative): boolean =>
  a.anchor !== undefined ? a.anchor?.source.kind === "exercise" : (a.last ?? null) !== null;

export type TodayDraftRow = {
  exerciseId: string;
  muscle: CatalogMuscleSlug;
  /** Today's gap (D3, regapTodayRows). */
  gap: number;
  /** Absent: "mev", and every draft saved before the fill rows. */
  target?: TodayTarget;
  /** As on TodayGap: what regapTodayRows reads. */
  targetSets?: number;
  weekSets?: number;
  /** 0 = out of today's time budget: the row waits for a longer one. */
  sets: number;
  lastTrainedDays: number | null;
  /** False: no history of its own, so kg starts empty (D2). A bodyweight
   *  exercise has history with reps and no kg. */
  hasHistory: boolean;
  /** AP-9c: every candidate the pain map allows for this muscle, in rank
   *  order, the current one included. The shuffle walks it. Absent on a
   *  draft saved before AP-9c. */
  alternatives?: TodayAlternative[];
};

/** A muscle marked «avoid» that kept an exercise out, and on which side. */
export type TodayPainCause = { slug: MuscleSlug; side: Side };

/** Display-only: rides on the PlanStarter and the logger draft, never on the
 *  saved workout (same contract as `coachNotes`). */
export type TodayProposalView = {
  /** #360. "cache": this device; "partial": and some rows found none. Absent: network. */
  loadSource?: "network" | "cache" | "partial";
  /** #360: what this device knew nothing about. "Don't know", not "never done". */
  unknownSlugs?: string[];
  /** The rest the draft kept. Absent: a draft saved before 48/72 h. */
  restHours?: number;
  /** LEGACY: what such a draft carries instead (4 calendar days). Read by
   *  nothing; a draft lives only until Start or discard. */
  restDays?: number;
  /** «Build one anyway»: rest ignored, the most rested muscles first. */
  ignoreRest?: boolean;
  /** AP-9c: the minutes the sets were fitted to. Absent: not fitted yet, every
   *  MEV row at its full clamp, every MAV row at 0 (the logger fits it on open). */
  minutes?: number;
  /** D5: the volume the gaps were scaled to, in percent. Absent: 100. */
  intensity?: number;
  /** D8: random picks instead of the most practised. Absent: off. */
  surprise?: boolean;
  /** #492 F5: the gaps were proposed bodyweight only (ProposalScope). Absent: off. */
  bodyweightOnly?: boolean;
  rows: TodayDraftRow[];
  /** MEV gap muscles left out because every candidate loads a painful muscle.
   *  A MAV muscle is never listed: it only fills spare time. */
  vetoedMuscles: CatalogMuscleSlug[];
  painCauses: TodayPainCause[];
  /** Muscles left out because they trained less than `restHours` ago, with
   *  the instant they come back. Absent on a draft saved before this field. */
  restingMuscles?: Array<{ muscle: CatalogMuscleSlug; returnMs: number }>;
  /** Every gap of the build, resting or not: what the rest chips filter
   *  (todayAtRest). Absent on a draft saved before the rest chips. */
  candidates?: TodayCandidate[];
};

// The veto reads the pain map through painRisk, the one place that knows
// which muscles an exercise loads (primary, secondary and joint overrides).
// ⛔ Not its severity: a yellow pain on a primary mover comes out red there,
// and yellow must exclude nothing. So the veto asks the athlete's own mark:
// «avoid» (red) on a side the exercise loads. The side never saves the
// exercise (a draft does not prescribe «only the right arm»).
const avoidCauses = (
  ex: Exercise,
  painMap: PainMap | undefined,
): TodayPainCause[] => {
  const risk = exercisePainSeverity(ex, painMap);
  if (!risk || !painMap) return [];
  return risk.painfulMuscles.flatMap((m) => {
    const marks = painSides(painMap[m.slug]);
    const red = BODY_SIDES.filter(
      (s) => marks[s] === "red" && (m.side === "both" || m.side === s),
    );
    const [only] = red;
    if (!only) return [];
    return [{ slug: m.slug, side: red.length === 2 ? "both" : only }];
  });
};

// D8: the exercise the athlete does most often first. A stable sort, so the
// upstream ranking (recruitment, gear, alsoCovers, slug) breaks the ties.
const byPractice = (
  candidates: TodayGap["candidates"],
  history: ReadonlyMap<string, TodayHistory>,
): TodayGap["candidates"] =>
  [...candidates].sort(
    (a, b) => (history.get(b.slug)?.practice ?? 0) - (history.get(a.slug)?.practice ?? 0),
  );

/** The planned entries of the rows in the draft (sets > 0): the anchor's reps
 *  at RIR 3 (D1), the load left to the effort arm of the default-weight
 *  chain, which reads the anchor set (todayChainSets). */
export const todayPlannedEntries = (
  rows: ReadonlyArray<TodayDraftRow>,
): PlannedExerciseEntry[] =>
  rows.flatMap((r) => {
    if (r.sets <= 0) return [];
    const alt = r.alternatives?.find((a) => a.exerciseId === r.exerciseId);
    const set = () =>
      alt?.anchor !== undefined
        ? {
            targetReps: alt.anchor?.reps ?? 0,
            targetWeight: { kind: "none" as const },
            targetRir: TODAY_TARGET_RIR,
          }
        : { targetReps: alt?.last?.reps ?? 0, targetWeight: { kind: "history" as const } };
    return [{ exerciseId: r.exerciseId, sets: Array.from({ length: r.sets }, set) }];
  });

/** One gap of the build, resting or not (Antenore, 07/10): the draft's rest
 *  chips filter these in place (todayAtRest), with no new build. */
export type TodayCandidate = {
  muscle: CatalogMuscleSlug;
  gap: number;
  /** As on TodayGap. */
  target?: TodayTarget;
  targetSets?: number;
  weekSets?: number;
  lastTrainedMs: number | null;
  /** What the pain map allows, the most practised first. Empty: none. */
  alternatives: TodayAlternative[];
  /** The «avoid» marks that kept an exercise out (MEV gaps only). */
  painCauses?: TodayPainCause[];
};

/** `alternatives`' first exercise no row above holds; `random`: a random one. */
export const freeTodayPick = (
  alternatives: ReadonlyArray<TodayAlternative>,
  taken: ReadonlySet<string>,
  random?: () => number,
): TodayAlternative | undefined => {
  const free = alternatives.filter((a) => !taken.has(a.exerciseId));
  return free[random ? Math.min(free.length - 1, Math.floor(random() * free.length)) : 0];
};

/** What the rest decides on a build, from its candidates: a muscle trained
 *  less than `restHours` ago rests, unless `ignoreRest` (then the least
 *  recently trained come first). MEV rows pick first. A muscle `held` holds
 *  (the draft's rows) keeps its exercise when free; the others take the first
 *  free one, or a random one with `random` («Surprise me»). Rows not regapped. */
export function todayAtRest(
  candidates: ReadonlyArray<TodayCandidate>,
  opts: {
    nowMs: number;
    restHours: number;
    ignoreRest?: boolean;
    held?: ReadonlyArray<TodayDraftRow>;
    random?: () => number;
  },
): Pick<
  TodayProposalView,
  "restHours" | "ignoreRest" | "rows" | "vetoedMuscles" | "painCauses" | "restingMuscles"
> {
  const restMs = opts.restHours * HOUR_MS;
  const ignoreRest = opts.ignoreRest === true;
  const rows: TodayDraftRow[] = [];
  const vetoedMuscles: CatalogMuscleSlug[] = [];
  const restingMuscles: Array<{ muscle: CatalogMuscleSlug; returnMs: number }> = [];
  const causes = new Map<MuscleSlug, TodayPainCause>();
  const used = new Set<string>();

  const restedFirst = (a: TodayCandidate, b: TodayCandidate) =>
    (a.lastTrainedMs ?? 0) - (b.lastTrainedMs ?? 0);
  const half = (mav: boolean) => {
    const out = candidates.filter((c) => (c.target === "mav") === mav);
    return ignoreRest ? out.sort(restedFirst) : out;
  };
  for (const c of [...half(false), ...half(true)]) {
    const mav = c.target === "mav";
    // The rest is elapsed hours; the age the row shows stays in calendar days.
    const last = c.lastTrainedMs;
    if (!ignoreRest && last !== null && opts.nowMs - last < restMs) {
      restingMuscles.push({ muscle: c.muscle, returnMs: last + restMs });
      continue;
    }
    const was = opts.held?.find((r) => r.muscle === c.muscle)?.exerciseId;
    const pick =
      c.alternatives.find((a) => a.exerciseId === was && !used.has(was)) ??
      freeTodayPick(c.alternatives, used, opts.random);
    if (!pick) {
      // No candidate at all is not a veto: the panel already says why.
      if (c.painCauses && c.painCauses.length > 0 && !mav) {
        vetoedMuscles.push(c.muscle);
        for (const b of c.painCauses) causes.set(b.slug, b);
      }
      continue;
    }
    used.add(pick.exerciseId);
    rows.push({
      exerciseId: pick.exerciseId,
      muscle: c.muscle,
      gap: c.gap,
      ...(mav ? { target: "mav" as const } : {}),
      ...(c.targetSets !== undefined ? { targetSets: c.targetSets } : {}),
      ...(c.weekSets !== undefined ? { weekSets: c.weekSets } : {}),
      sets: mav ? 0 : todaySetCap(pick.setsToClose),
      lastTrainedDays: last === null ? null : calendarDaysBetween(last, opts.nowMs),
      hasHistory: altHasHistory(pick),
      alternatives: c.alternatives,
    });
  }
  return {
    restHours: opts.restHours,
    ...(ignoreRest ? { ignoreRest: true } : {}),
    rows,
    vetoedMuscles,
    painCauses: [...causes.values()],
    restingMuscles,
  };
}

export function proposeTodaySession(input: {
  gaps: ReadonlyArray<TodayGap>;
  catalog: ReadonlyMap<string, Exercise>;
  painMap: PainMap | undefined;
  /** D1, D8: each candidate's anchor and practice (todayAnchor.ts). A slug
   *  missing from it was never done. */
  history: ReadonlyMap<string, TodayHistory>;
  nowMs: number;
  /** Absent: DEFAULT_REST_HOURS. */
  restHours?: number;
  /** «Build one anyway»: no muscle rests, and the least recently trained
   *  come first (never trained before all). */
  ignoreRest?: boolean;
}): { entries: PlannedExerciseEntry[]; view: TodayProposalView } {
  const candidates = input.gaps.map((g): TodayCandidate => {
    const alternatives: TodayAlternative[] = [];
    const blocked: TodayPainCause[] = [];
    for (const c of byPractice(g.candidates, input.history)) {
      const ex = input.catalog.get(c.slug);
      if (!ex) continue;
      const found = avoidCauses(ex, input.painMap);
      if (found.length > 0) {
        blocked.push(...found);
        continue;
      }
      alternatives.push({
        exerciseId: c.slug,
        setsToClose: c.setsToClose,
        anchor: input.history.get(c.slug)?.anchor ?? null,
      });
    }
    return {
      muscle: g.muscle,
      gap: g.gap,
      ...(g.target === "mav" ? { target: "mav" as const } : {}),
      ...(g.targetSets !== undefined ? { targetSets: g.targetSets } : {}),
      ...(g.weekSets !== undefined ? { weekSets: g.weekSets } : {}),
      lastTrainedMs: g.lastTrainedMs,
      alternatives,
      ...(blocked.length > 0 && g.target !== "mav" ? { painCauses: blocked } : {}),
    };
  });
  const atRest = todayAtRest(candidates, {
    nowMs: input.nowMs,
    restHours: input.restHours ?? DEFAULT_REST_HOURS,
    ignoreRest: input.ignoreRest,
  });
  // Today's gaps at 100%: the logger scales them to the athlete's intensity.
  const regapped = regapTodayRows(atRest.rows, 100, input.catalog);
  return {
    entries: todayPlannedEntries(regapped),
    view: { ...atRest, rows: regapped, candidates },
  };
}
